import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { theme, neutral, jadeGreen, goldenTopaz, lightBlue } from '../design-system/theme';
import { 
  adminApi, 
  LibraryTemplate, 
  GeographyInfo, 
  RegulationInfo, 
  CategoryInfo 
} from '../services/adminApi';
import AdminShell from '../components/admin/AdminShell';
import {
  PageContainer,
  FilterGroup,
  SelectInput,
  PrimaryButton,
  SecondaryButton,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Tag,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner,
} from '../styles/adminShared';

// Icon components (inline SVGs)
const IconArrowLeft: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const IconFilter: React.FC<{ color?: string }> = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" fill="none" stroke={color} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const IconGlobe: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconShield: React.FC<{ size?: number }> = ({ size = 10 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const IconTag: React.FC<{ size?: number }> = ({ size = 10 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

const IconStar: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const IconDownload: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const IconInfo: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconX: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const IconCheckCircle: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconClock: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconAlertCircle: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Page-specific styled components
const PageContainerWithGap = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid ${neutral[4]};
  border-radius: 8px;
  color: ${theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${neutral[1]};
    border-color: ${neutral[5]};
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${theme.textPrimary};
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${neutral[6]};
`;

const FilterToggle = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? theme.primary : neutral[4]};
  border-radius: 8px;
  background: ${props => props.$active ? `rgba(234, 88, 44, 0.1)` : 'white'};
  color: ${props => props.$active ? theme.primary : theme.textPrimary};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${theme.primary};
  }
`;

const ClearFilters = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: none;
  color: ${neutral[6]};
  font-size: 13px;
  cursor: pointer;
  margin-left: auto;
  
  &:hover {
    background: ${neutral[1]};
    color: ${theme.textPrimary};
  }
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
`;

const TemplateCard = styled(Card)<{ $featured?: boolean }>`
  border: 1px solid ${props => props.$featured ? theme.primary : neutral[4]};
  transition: all 0.2s;
  position: relative;
  
  ${props => props.$featured && `
    box-shadow: 0 0 0 1px ${theme.primary}, 0 4px 6px rgba(234, 88, 44, 0.1);
  `}
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
  text-transform: uppercase;
`;

const TemplateCardHeader = styled(CardHeader)`
  padding: 20px;
`;

const RegulationBadge = styled.span<{ $regulation: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.$regulation) {
      case 'DPDP':
        return `background: ${lightBlue[1]}; color: ${lightBlue[7]};`;
      case 'GDPR':
        return `background: ${jadeGreen[1]}; color: ${jadeGreen[7]};`;
      case 'CCPA':
        return `background: ${goldenTopaz[1]}; color: ${goldenTopaz[8]};`;
      case 'POPIA':
        return `background: #f3e8ff; color: #7c3aed;`;
      default:
        return `background: ${neutral[2]}; color: ${neutral[7]};`;
    }
  }}
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${neutral[2]};
  color: ${neutral[6]};
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const TemplateName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.textPrimary};
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const TemplateDescription = styled.p`
  font-size: 13px;
  color: ${neutral[6]};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TemplateCardBody = styled(CardBody)`
  padding: 16px 20px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaLabel = styled.span`
  font-size: 11px;
  color: ${neutral[5]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetaValue = styled.span`
  font-size: 13px;
  color: ${theme.textPrimary};
  font-weight: 500;
`;

const TagsRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const TemplateTag = styled(Tag)`
  padding: 2px 8px;
  background: #f9fafb;
  border: 1px solid ${neutral[4]};
  border-radius: 4px;
  font-size: 11px;
  color: ${neutral[6]};
`;

const TemplateCardFooter = styled(CardFooter)`
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  background: #f9fafb;
  border-top: 1px solid ${neutral[3]};
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: ${props => props.$primary ? 'none' : `1px solid ${neutral[4]}`};
  border-radius: 8px;
  background: ${props => props.$primary ? theme.primary : 'white'};
  color: ${props => props.$primary ? 'white' : theme.textPrimary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.$primary ? '#d14d25' : neutral[1]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalContentStyled = styled(ModalContent)`
  max-width: 600px;
`;

const DetailSection = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${neutral[6]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px 0;
`;

const DetailText = styled.p`
  font-size: 14px;
  color: ${theme.textPrimary};
  margin: 0;
  line-height: 1.6;
`;

const ComplianceBox = styled.div`
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 8px;
`;

const ComplianceTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 4px;
`;

const ComplianceText = styled.p`
  font-size: 13px;
  color: #78350f;
  margin: 0;
  line-height: 1.5;
`;

const SuccessMessage = styled(AlertMessage)`
  margin-bottom: 16px;
`;

// Main Component
const TemplateLibrary: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [templates, setTemplates] = useState<LibraryTemplate[]>([]);
  const [geographies, setGeographies] = useState<GeographyInfo[]>([]);
  const [regulations, setRegulations] = useState<RegulationInfo[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  
  // Filters
  const [selectedRegulation, setSelectedRegulation] = useState<string>('');
  const [selectedGeography, setSelectedGeography] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showFeatured, setShowFeatured] = useState(false);
  
  // Modal
  const [selectedTemplate, setSelectedTemplate] = useState<LibraryTemplate | null>(null);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [geos, regs, cats] = await Promise.all([
        adminApi.getAvailableGeographies(),
        adminApi.getAvailableRegulations(),
        adminApi.getAvailableCategories(),
      ]);
      setGeographies(geos);
      setRegulations(regs);
      setCategories(cats);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getLibraryTemplates({
        regulation: selectedRegulation || undefined,
        geography: selectedGeography || undefined,
        category: selectedCategory || undefined,
        featured: showFeatured || undefined,
      });
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedRegulation, selectedGeography, selectedCategory, showFeatured]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleImport = async (template: LibraryTemplate) => {
    setImporting(template.templateId);
    try {
      await adminApi.importLibraryTemplate({
        regulation: template.regulation,
        libraryTemplateId: template.templateId,
      });
      setImportSuccess(template.templateId);
      setTimeout(() => {
        setImportSuccess(null);
        setSelectedTemplate(null);
        navigate('/admin/templates');
      }, 1500);
    } catch (error) {
      console.error('Error importing template:', error);
      alert('Failed to import template. Please try again.');
    } finally {
      setImporting(null);
    }
  };

  const clearFilters = () => {
    setSelectedRegulation('');
    setSelectedGeography('');
    setSelectedCategory('');
    setShowFeatured(false);
  };

  const hasFilters = selectedRegulation || selectedGeography || selectedCategory || showFeatured;

  return (
    <AdminShell>
      <PageContainerWithGap>
        {/* Page Header */}
        <PageHeader>
          <BackButton onClick={() => navigate('/admin/templates')}>
            <IconArrowLeft size={16} />
            Back to Templates
          </BackButton>
          <PageTitle>Template Library</PageTitle>
        </PageHeader>
        
        {/* Filters */}
        <FiltersBar>
          <IconFilter color={neutral[6]} />
          
          <FilterGroup>
            <FilterLabel>Regulation</FilterLabel>
            <SelectInput
              as="select"
              value={selectedRegulation} 
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSelectedRegulation(e.target.value)}
            >
              <option value="">All Regulations</option>
              {regulations.map(r => (
                <option key={r.code} value={r.code}>
                  {r.displayName} ({r.templateCount})
                </option>
              ))}
            </SelectInput>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Geography</FilterLabel>
            <SelectInput
              as="select"
              value={selectedGeography} 
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSelectedGeography(e.target.value)}
            >
              <option value="">All Geographies</option>
              {geographies.map(g => (
                <option key={g.code} value={g.code}>
                  {g.displayName} ({g.templateCount})
                </option>
              ))}
            </SelectInput>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Category</FilterLabel>
            <SelectInput
              as="select"
              value={selectedCategory} 
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(c => (
                <option key={c.code} value={c.code}>
                  {c.displayName} ({c.templateCount})
                </option>
              ))}
            </SelectInput>
          </FilterGroup>
          
          <FilterToggle $active={showFeatured} onClick={() => setShowFeatured(!showFeatured)}>
            <IconStar size={14} />
            Featured Only
          </FilterToggle>
          
          {hasFilters && (
            <ClearFilters onClick={clearFilters}>
              <IconX size={14} />
              Clear Filters
            </ClearFilters>
          )}
        </FiltersBar>

        {/* Template Grid */}
        {loading ? (
          <LoadingSpinner>Loading templates...</LoadingSpinner>
        ) : templates.length === 0 ? (
          <EmptyStateContainer>
            <EmptyStateIcon>🌍</EmptyStateIcon>
            <EmptyStateTitle>No templates found</EmptyStateTitle>
            <EmptyStateText>Try adjusting your filters or check back later for new templates.</EmptyStateText>
          </EmptyStateContainer>
        ) : (
          <TemplateGrid>
            {templates.map(template => (
              <TemplateCard key={`${template.regulation}-${template.templateId}`} $featured={template.featured}>
                {template.featured && (
                  <FeaturedBadge>
                    <IconStar size={10} />
                    Featured
                  </FeaturedBadge>
                )}
                
                <TemplateCardHeader>
                  <BadgeRow>
                    <RegulationBadge $regulation={template.regulation}>
                      <IconShield size={10} />
                      {template.regulation}
                    </RegulationBadge>
                    <CategoryBadge>
                      <IconTag size={10} />
                      {template.category}
                    </CategoryBadge>
                  </BadgeRow>
                  <TemplateName>{template.name}</TemplateName>
                  <TemplateDescription>{template.description}</TemplateDescription>
                </TemplateCardHeader>
                
                <TemplateCardBody>
                  <MetaGrid>
                    <MetaItem>
                      <MetaLabel>Geography</MetaLabel>
                      <MetaValue>{template.geography}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                      <MetaLabel>Version</MetaLabel>
                      <MetaValue>{template.version}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                      <MetaLabel>Expiry</MetaLabel>
                      <MetaValue>{template.recommendedExpiryDays || '—'} days</MetaValue>
                    </MetaItem>
                    <MetaItem>
                      <MetaLabel>Imports</MetaLabel>
                      <MetaValue>{template.usageCount || 0}</MetaValue>
                    </MetaItem>
                  </MetaGrid>
                  
                  {template.tags && template.tags.length > 0 && (
                    <TagsRow>
                      {template.tags.slice(0, 4).map(tag => (
                        <TemplateTag key={tag}>{tag}</TemplateTag>
                      ))}
                    </TagsRow>
                  )}
                </TemplateCardBody>
                
                <TemplateCardFooter>
                  <ActionButton onClick={() => setSelectedTemplate(template)}>
                    <IconInfo size={14} />
                    View Details
                  </ActionButton>
                  <ActionButton 
                    $primary 
                    onClick={() => handleImport(template)}
                    disabled={importing === template.templateId}
                  >
                    {importSuccess === template.templateId ? (
                      <>
                        <IconCheckCircle size={14} />
                        Imported!
                      </>
                    ) : importing === template.templateId ? (
                      <>
                        <IconClock size={14} />
                        Importing...
                      </>
                    ) : (
                      <>
                        <IconDownload size={14} />
                        Import
                      </>
                    )}
                  </ActionButton>
                </TemplateCardFooter>
              </TemplateCard>
            ))}
          </TemplateGrid>
        )}

        {/* Detail Modal */}
        {selectedTemplate && (
          <ModalOverlay $show={!!selectedTemplate} onClick={() => setSelectedTemplate(null)}>
            <ModalContentStyled onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{selectedTemplate.name}</ModalTitle>
                <CloseButton onClick={() => setSelectedTemplate(null)}>
                  <IconX size={18} />
                </CloseButton>
              </ModalHeader>
              
              <ModalBody>
                {importSuccess === selectedTemplate.templateId && (
                  <SuccessMessage $variant="success">
                    <IconCheckCircle size={18} />
                    Template imported successfully! Redirecting to Templates...
                  </SuccessMessage>
                )}
                
                <DetailSection>
                  <BadgeRow>
                    <RegulationBadge $regulation={selectedTemplate.regulation}>
                      <IconShield size={10} />
                      {selectedTemplate.regulation}
                    </RegulationBadge>
                    <CategoryBadge>
                      <IconGlobe size={10} />
                      {selectedTemplate.geography}
                    </CategoryBadge>
                    <CategoryBadge>
                      <IconTag size={10} />
                      {selectedTemplate.category}
                    </CategoryBadge>
                  </BadgeRow>
                </DetailSection>
                
                <DetailSection>
                  <DetailLabel>Description</DetailLabel>
                  <DetailText>{selectedTemplate.description}</DetailText>
                </DetailSection>
                
                {selectedTemplate.legalBasis && (
                  <DetailSection>
                    <DetailLabel>Legal Basis</DetailLabel>
                    <DetailText>{selectedTemplate.legalBasis}</DetailText>
                  </DetailSection>
                )}
                
                {selectedTemplate.complianceNotes && (
                  <DetailSection>
                    <DetailLabel>Compliance Notes</DetailLabel>
                    <ComplianceBox>
                      <ComplianceTitle>
                        <IconAlertCircle size={14} />
                        Important
                      </ComplianceTitle>
                      <ComplianceText>{selectedTemplate.complianceNotes}</ComplianceText>
                    </ComplianceBox>
                  </DetailSection>
                )}
                
                {selectedTemplate.dataCategories && selectedTemplate.dataCategories.length > 0 && (
                  <DetailSection>
                    <DetailLabel>Data Categories</DetailLabel>
                    <TagsRow>
                      {selectedTemplate.dataCategories.map(cat => (
                        <TemplateTag key={cat}>{cat}</TemplateTag>
                      ))}
                    </TagsRow>
                  </DetailSection>
                )}
                
                {selectedTemplate.primaryPurposes && selectedTemplate.primaryPurposes.length > 0 && (
                  <DetailSection>
                    <DetailLabel>Primary Purposes</DetailLabel>
                    <ul style={{ margin: 0, paddingLeft: 20, color: theme.textPrimary, fontSize: 14 }}>
                      {selectedTemplate.primaryPurposes.map((purpose, i) => (
                        <li key={i}>{purpose}</li>
                      ))}
                    </ul>
                  </DetailSection>
                )}
                
                {selectedTemplate.mandatoryFields && selectedTemplate.mandatoryFields.length > 0 && (
                  <DetailSection>
                    <DetailLabel>Mandatory Fields</DetailLabel>
                    <TagsRow>
                      {selectedTemplate.mandatoryFields.map(field => (
                        <TemplateTag key={field}>{field.replace(/_/g, ' ')}</TemplateTag>
                      ))}
                    </TagsRow>
                  </DetailSection>
                )}
                
                <MetaGrid style={{ marginTop: 20 }}>
                  <MetaItem>
                    <MetaLabel>Recommended Expiry</MetaLabel>
                    <MetaValue>{selectedTemplate.recommendedExpiryDays || '—'} days</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Requires Reconsent</MetaLabel>
                    <MetaValue>{selectedTemplate.requiresReconsent ? 'Yes' : 'No'}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Version</MetaLabel>
                    <MetaValue>{selectedTemplate.version}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Times Imported</MetaLabel>
                    <MetaValue>{selectedTemplate.usageCount || 0}</MetaValue>
                  </MetaItem>
                </MetaGrid>
              </ModalBody>
              
              <ModalFooter>
                <SecondaryButton onClick={() => setSelectedTemplate(null)}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton 
                  onClick={() => handleImport(selectedTemplate)}
                  disabled={importing === selectedTemplate.templateId || importSuccess === selectedTemplate.templateId}
                >
                  {importSuccess === selectedTemplate.templateId ? (
                    <>
                      <IconCheckCircle size={14} />
                      Imported!
                    </>
                  ) : importing === selectedTemplate.templateId ? (
                    <>
                      <IconClock size={14} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <IconDownload size={14} />
                      Import Template
                    </>
                  )}
                </PrimaryButton>
              </ModalFooter>
            </ModalContentStyled>
          </ModalOverlay>
        )}
      </PageContainerWithGap>
    </AdminShell>
  );
};

export default TemplateLibrary;
